import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, Menu, X, Phone, MessageCircle, User, LogIn, LogOut, ChevronDown } from "lucide-react";
import { useSound } from "../../hooks/useSound";
import { useStudentAuth } from "../../context/StudentAuth";

import { Logo } from "./Logo";
import { CONTACT } from "../../lib/content";

const LINKS = [
  ["About", "#about"],
  ["Courses", "#courses"],
  ["Portfolio", "#media"],
  ["Testimonials", "#testimonials"],
  ["Contact", "#contact"],
];

const scrollTo = (hash) => {
  if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -60, duration: 1.4 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { enabled, toggle, pluck } = useSound();
  const { user, logout } = useStudentAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const doLogout = () => { setMenuOpen(false); logout(); nav("/"); };

  const go = (hash) => {
    setOpen(false);
    setTimeout(() => scrollTo(hash), 50);
  };

  return (
    <>
      <motion.nav
        data-testid="main-navbar"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#f7f6f2]/85 border-b border-[rgba(28,22,16,0.12)]"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <button data-testid="nav-logo" onClick={() => scrollTo("#hero")}>
            <Logo />
          </button>

          <div className="hidden md:flex items-center gap-10">
            {LINKS.map(([label, hash]) => (
              <button
                key={hash}
                data-testid={`nav-link-${label.toLowerCase()}`}
                onClick={() => go(hash)}
                className="string-link px-1 py-3 font-mono text-xs tracking-[0.2em] uppercase text-[#4a3e30] hover:text-[#1c1610] transition-colors duration-300"
              >
                {label}
                <svg viewBox="0 0 100 10" preserveAspectRatio="none"><path /></svg>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="sound-toggle-btn"
              onClick={toggle}
              title={enabled ? "Mute string sounds" : "Enable string sounds"}
              className="w-9 h-9 flex items-center justify-center border border-[rgba(28,22,16,0.2)] text-[#4a3e30] hover:text-[#b3271e] hover:border-[#b3271e] transition-colors duration-300"
            >
              {enabled ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
            {user ? (
              <div
                ref={menuRef}
                className="hidden sm:block relative"
                onMouseEnter={() => setMenuOpen(true)}
                onMouseLeave={() => setMenuOpen(false)}
              >
                <button
                  data-testid="nav-profile-link"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 border font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300 ${
                    menuOpen
                      ? "bg-[#DB0909] border-[#DB0909] text-[#f7f6f2]"
                      : "bg-[#1c1610] border-[#1c1610] text-[#f7f6f2] hover:bg-[#DB0909] hover:border-[#DB0909]"
                  }`}
                >
                  <span
                    aria-hidden
                    className="w-7 h-7 rounded-full overflow-hidden bg-gradient-to-br from-[#DB0909] to-[#950606] text-[#f7f6f2] font-mono text-[11px] font-bold flex items-center justify-center shrink-0 border border-[#f7f6f2]/25"
                  >
                    {user.profile_image ? (
                      <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      (user.name || user.username || "?")[0].toUpperCase()
                    )}
                  </span>
                  <span className="tracking-[0.15em]">Hey {(user.name || "").split(" ")[0] || user.username}</span>
                  <ChevronDown size={12} className={`transition-transform duration-200 ${menuOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      data-testid="nav-profile-menu"
                      initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                      role="menu"
                      /* invisible hover-bridge above the panel so the mouse doesn't leave when travelling from the button */
                      className="absolute right-0 top-full pt-2 w-52"
                    >
                      <div className="border border-[rgba(28,22,16,0.15)] bg-[#f7f6f2] shadow-[6px_6px_0_rgba(28,22,16,0.12)]">
                        <div className="px-4 py-3 border-b border-[rgba(28,22,16,0.1)]">
                          <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#4a3e30]">Signed in as</p>
                          <p className="mt-0.5 font-display font-bold text-sm text-[#1c1610] truncate">@{user.username}</p>
                        </div>
                        <Link
                          to="/profile"
                          role="menuitem"
                          data-testid="nav-profile-menu-profile"
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-[#1c1610] hover:bg-[#DB0909] hover:text-[#f7f6f2] transition-colors"
                        >
                          <User size={14} />
                          <span className="font-mono text-[11px] tracking-[0.2em] uppercase">Profile</span>
                        </Link>
                        <button
                          role="menuitem"
                          data-testid="nav-profile-menu-signout"
                          onClick={doLogout}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#1c1610] hover:bg-[#DB0909] hover:text-[#f7f6f2] transition-colors border-t border-[rgba(28,22,16,0.08)]"
                        >
                          <LogOut size={14} />
                          <span className="font-mono text-[11px] tracking-[0.2em] uppercase">Sign out</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                data-testid="nav-profile-link"
                to="/login"
                title="Sign in or register"
                className="hidden sm:inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase border border-[rgba(28,22,16,0.25)] text-[#1c1610] px-4 py-2.5 hover:border-[#DB0909] hover:text-[#DB0909] transition-colors duration-300"
              >
                <LogIn size={13} /> Sign in · Register
              </Link>
            )}

            {/* Mobile compact profile / sign-in pill — always visible in top nav */}
            {user ? (
              <Link
                data-testid="mobile-nav-profile-pill"
                to="/profile"
                title={`Hey ${(user.name || "").split(" ")[0] || user.username} — your profile`}
                className="sm:hidden inline-flex items-center gap-1.5 pl-0.5 pr-2 py-1 bg-[#1c1610] text-[#f7f6f2] font-mono text-[10px] tracking-[0.12em] uppercase"
              >
                <span aria-hidden className="w-6 h-6 rounded-full overflow-hidden bg-gradient-to-br from-[#DB0909] to-[#950606] font-bold text-[10px] flex items-center justify-center border border-[#f7f6f2]/20">
                  {user.profile_image ? (
                    <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (user.name || user.username || "?")[0].toUpperCase()
                  )}
                </span>
                <span className="tracking-[0.12em]">{(user.name || "").split(" ")[0] || user.username}</span>
              </Link>
            ) : (
              <Link
                data-testid="mobile-nav-signin-pill"
                to="/login"
                title="Sign in or register"
                className="sm:hidden inline-flex items-center gap-1.5 border border-[rgba(28,22,16,0.28)] text-[#1c1610] font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 active:border-[#DB0909] active:text-[#DB0909] transition-colors"
              >
                <LogIn size={11} /> Sign in
              </Link>
            )}
            <button
              data-testid="nav-book-btn"
              onMouseEnter={() => pluck(246.9)}
              onClick={() => go("#contact")}
              className="vibe hidden sm:block font-mono text-xs tracking-[0.2em] uppercase bg-[#1c1610] text-[#f7f6f2] px-5 py-2.5 hover:bg-[#DB0909] active:bg-[#950606] transition-colors duration-300"
            >
              Book a Spot
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(!open)}
              className="md:hidden w-9 h-9 flex items-center justify-center text-[#1c1610]"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-[#f7f6f2]/97 backdrop-blur-xl flex flex-col justify-center px-10 gap-2 md:hidden overflow-y-auto py-24"
          >
            {LINKS.map(([label, hash], i) => (
              <motion.button
                key={hash}
                data-testid={`mobile-nav-link-${label.toLowerCase()}`}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => go(hash)}
                className="text-left font-display text-4xl font-bold text-[#1c1610] py-3 border-b border-[rgba(28,22,16,0.12)]"
              >
                {label}
              </motion.button>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + LINKS.length * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 flex flex-col gap-3"
            >
              <button
                data-testid="mobile-nav-book-btn"
                onClick={() => go("#contact")}
                className="w-full font-mono text-xs tracking-[0.25em] uppercase bg-[#1c1610] text-[#f7f6f2] py-4 active:bg-[#DB0909] transition-colors duration-300"
              >
                Book a Spot →
              </button>
              <Link
                data-testid="mobile-nav-profile-link"
                to={user ? "/profile" : "/login"}
                onClick={() => setOpen(false)}
                className={`w-full flex items-center justify-center gap-3 py-4 font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                  user
                    ? "bg-[#1c1610] text-[#f7f6f2] active:bg-[#DB0909]"
                    : "border border-[rgba(28,22,16,0.35)] text-[#1c1610] active:border-[#DB0909] active:text-[#DB0909]"
                }`}
              >
                {user ? (
                  <>
                    <span aria-hidden className="w-7 h-7 rounded-full overflow-hidden bg-gradient-to-br from-[#DB0909] to-[#950606] text-[#f7f6f2] font-mono text-[11px] font-bold flex items-center justify-center border border-[#f7f6f2]/25">
                      {user.profile_image ? (
                        <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (user.name || user.username || "?")[0].toUpperCase()
                      )}
                    </span>
                    <span>Hey {(user.name || "").split(" ")[0] || user.username}</span>
                  </>
                ) : (
                  <><LogIn size={14} /> Sign in · Register</>
                )}
              </Link>
              {user && (
                <button
                  data-testid="mobile-nav-signout"
                  onClick={() => { setOpen(false); logout(); nav("/"); }}
                  className="w-full flex items-center justify-center gap-2 py-3 font-mono text-xs tracking-[0.2em] uppercase text-[#4a3e30] hover:text-[#DB0909] transition-colors duration-300"
                >
                  <LogOut size={13} /> Sign out
                </button>
              )}
              <div className="grid grid-cols-2 gap-3">
                <a
                  data-testid="mobile-nav-call-link"
                  href={`tel:${CONTACT.phoneRaw}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 font-mono text-xs tracking-[0.2em] uppercase border border-[rgba(28,22,16,0.35)] text-[#1c1610] py-4 active:border-[#950606] active:text-[#950606] transition-colors duration-300"
                >
                  <Phone size={14} /> Call
                </a>
                <a
                  data-testid="mobile-nav-whatsapp-link"
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 font-mono text-xs tracking-[0.2em] uppercase border border-[rgba(28,22,16,0.35)] text-[#1c1610] py-4 active:border-[#950606] active:text-[#950606] transition-colors duration-300"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>
              <p className="mt-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30] text-center">
                {CONTACT.phone}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
